<template>
  <div class="conversation-list">
    <div class="message-header">
      <div class="message-title-row">
        <div>
          <div class="message-title">消息</div>
          <div class="message-subtitle">最近联系 {{ conversations.length }} 个会话</div>
        </div>
        <button class="message-action" @click="onRefresh">
          <van-icon name="replay" />
        </button>
      </div>

      <div class="message-tabs">
        <div class="message-tab" :class="{ active: activeTab === 'chat' }" @click="switchTab('chat')">私信</div>
        <div class="message-tab notice-tab" :class="{ active: activeTab === 'notice' }" @click="switchTab('notice')">
          系统通知
          <span v-if="noticeUnreadCount > 0" class="tab-unread-dot"></span>
        </div>
      </div>
    </div>

    <div class="conversation-content" v-if="hasContent">
      <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
        <van-list v-model="loading" :finished="finished" @load="onLoad">
          <div v-if="activeTab === 'chat'" class="conversation-item" v-for="item in conversations" :key="item.id"
            @click="toChatRoom(item)">
            <div class="avatar-wrapper">
              <van-image width="100%" height="100%" :src="getAvatar(item)" fit="cover" />
              <span class="unread-badge" v-if="item.unreadCount > 0">
                {{ item.unreadCount > 99 ? '99+' : item.unreadCount }}
              </span>
            </div>

            <div class="info-wrapper">
              <div class="top-row">
                <span class="name">{{ getName(item) }}</span>
                <span class="time">{{ getRelativeTime(item.lastMessageTime) }}</span>
              </div>
              <div class="bottom-row">
                <span class="last-message">{{ item.lastMessageContent || '暂无消息' }}</span>
              </div>
            </div>

            <div class="message-arrow">
              <van-icon name="arrow" />
            </div>
          </div>
          <div v-if="activeTab === 'notice'" class="conversation-item notice-item" v-for="item in notices"
            :key="item.id" @click="openNotice(item)">
            <div class="avatar-wrapper notice-avatar-wrapper">
              <van-icon name="volume-o" />
              <span class="unread-badge" v-if="!item.isRead">1</span>
            </div>

            <div class="info-wrapper">
              <div class="top-row">
                <span class="name">{{ item.title || '系统通知' }}</span>
                <span class="time">{{ getRelativeTime(item.createOn) }}</span>
              </div>
              <div class="bottom-row">
                <span class="last-message">{{ item.content || '暂无内容' }}</span>
              </div>
            </div>

            <div class="message-arrow">
              <van-icon name="arrow" />
            </div>
          </div>
        </van-list>
      </van-pull-refresh>
    </div>

    <div class="empty-state" v-else>
      <p class="empty-text">{{ activeTab === 'chat' ? '暂无消息' : '暂无系统通知' }}</p>
      <p class="empty-hint">{{ activeTab === 'chat' ? getTip() : '后续通知会显示在这里' }}</p>
    </div>

    <TabBar active-key="message" v-if="role === 'Member' || role === 'Matchmaker'" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import TabBar from "@/components/TabBar.vue";
import { getSystemNoticePageList, getSystemNoticeUnreadCount, markSystemNoticeAsRead } from '@/api/systemNotice';
import { getTimeStr, getRelativeTime } from "@/utils/tools";

export default {
  name: "ConversationList",
  components: {
    TabBar
  },
  data() {
    return {
      activeTab: 'chat',
      loading: false,
      finished: true,
      refreshing: false,
      conversations: [],
      notices: [],
      noticePage: 1,
      noticePageSize: 20,
      noticeUnreadCount: 0
    };
  },
  computed: {
    ...mapGetters(["role"]),
    hasContent() {
      return this.activeTab === 'chat' ? this.conversations.length > 0 : this.notices.length > 0;
    }
  },
  created() {
    this.loadConversations();
    this.loadNoticeUnreadCount();
  },
  activated() {
    this.loadConversations();
    this.loadNoticeUnreadCount();
  },
  methods: {
    ...mapActions([
      "GetConversations",
      "MarkAsRead",
      "SetCurrentConversation"
    ]),
    getTimeStr,
    getRelativeTime,
    async loadConversations() {
      try {
        const res = await this.GetConversations();
        this.conversations = res || [];
      } catch (error) {
        console.error("获取会话列表失败:", error);
        this.$notify({
          type: "danger",
          message: "获取消息列表失败"
        });
      }
    },
    async loadNotices() {
      try {
        const res = await getSystemNoticePageList({
          page: this.noticePage,
          pageSize: this.noticePageSize
        });
        this.notices = (res.data && res.data.items) || [];
        this.noticeUnreadCount = this.notices.filter(item => !item.isRead).length;
      } catch (error) {
        console.error('获取系统通知失败:', error);
        this.$notify({
          type: 'danger',
          message: '获取系统通知失败'
        });
      }
    },
    async loadNoticeUnreadCount() {
      try {
        const res = await getSystemNoticeUnreadCount();
        this.noticeUnreadCount = res.data || 0;
      } catch (error) {
        console.error('获取系统通知未读数失败:', error);
      }
    },
    switchTab(tab) {
      if (this.activeTab === tab) {
        return;
      }
      this.activeTab = tab;
      if (tab === 'chat') {
        this.loadConversations();
      } else {
        this.loadNotices();
        this.loadNoticeUnreadCount();
      }
    },
    async onRefresh() {
      if (this.activeTab === 'chat') {
        await this.loadConversations();
      } else {
        await this.loadNotices();
        await this.loadNoticeUnreadCount();
      }
      this.refreshing = false;
    },
    onLoad() {
      this.loading = false;
      this.finished = true;
    },
    toChatRoom(item) {
      this.MarkAsRead(item.id);
      this.SetCurrentConversation(item);
      this.$router.push({
        path: `/chatRoom/${item.id}`,
        query: {
          matchmakerId: item.matchmakerId,
          memberId: item.memberId,
          matchmakerName: item.matchmakerName,
          memberName: item.memberName,
          matchmakerAvatar: item.matchmakerAvatar,
          memberAvatar: item.memberAvatar
        }
      });
    },
    async openNotice(item) {
      if (!item.isRead) {
        try {
          await markSystemNoticeAsRead({ noticeId: item.id });
          item.isRead = true;
          this.noticeUnreadCount = Math.max(0, this.noticeUnreadCount - 1);
        } catch (error) {
          console.error('标记系统通知已读失败:', error);
        }
      }
      this.$dialog.alert({
        title: item.title || '系统通知',
        message: item.content || '暂无内容'
      });
    },
    getAvatar(item) {
      if (this.role === "Member") {
        return item.matchmakerAvatar || "";
      }
      return item.memberAvatar || "";
    },
    getName(item) {
      if (this.role === "Member") {
        return item.matchmakerName || "红娘";
      }
      return item.memberName || "会员";
    },
    getTip() {
      return this.role === "Member" ? "联系红娘开始聊天吧" : "联系会员开始聊天吧";
    }
  }
};
</script>

<style scoped lang="less" src="../style/conversationList.less" />



// WEBPACK FOOTER //
// src/views/conversationList.vue